import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Boxes, CalendarClock, ScrollText, Camera, SlidersHorizontal,
  Upload, FileDown, ShieldCheck, AlertTriangle, ArrowLeftRight, PlusCircle,
} from "lucide-react";

const TABS = [
  { key: "today", label: "Today", icon: Boxes },
  { key: "historical", label: "Historical", icon: CalendarClock },
  { key: "ledger", label: "Movement Ledger", icon: ScrollText },
  { key: "snapshots", label: "Snapshots", icon: Camera },
  { key: "adjustments", label: "Adjustments", icon: SlidersHorizontal },
  { key: "reconcile", label: "Reconciliation", icon: ShieldCheck },
  { key: "compare", label: "Compare", icon: ArrowLeftRight },
  { key: "legacy", label: "Legacy Import", icon: Upload },
];

const API = process.env.REACT_APP_BACKEND_URL;
const dl = (path, name) => {
  const a = document.createElement("a");
  a.href = `${API}/api${path}`;
  a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
};

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));
const today = () => new Date().toISOString().slice(0, 10);

export default function DailyStock() {
  const [tab, setTab] = useState("today");
  const [overview, setOverview] = useState(null);

  const loadOverview = useCallback(async () => {
    try { const { data } = await api.get("/daily-stock/overview"); setOverview(data); }
    catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="daily-stock-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <Boxes className="h-6 w-6 text-emerald-700" /> Daily Stock Control
          </h1>
          <p className="text-sm text-slate-500">Live position, historical snapshots and an audit-ready movement register.</p>
        </div>
        {overview && (
          <div className="flex gap-2 text-[11px]">
            <Stat label="Recon" value={overview.today_recon_status} testid="ds-ov-recon" />
            <Stat label="Exceptions" value={overview.exceptions_today} tone={overview.exceptions_today ? "warn" : "ok"} testid="ds-ov-exceptions" />
            <Stat label="Pending adj." value={overview.pending_adjustments} testid="ds-ov-pending" />
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`ds-tab-${t.key}`}
            className={`px-3 py-2 text-[13px] font-medium flex items-center gap-1.5 whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t.key ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "today" && <PositionView mode="today" overview={overview} onChange={loadOverview} />}
      {tab === "historical" && <PositionView mode="historical" />}
      {tab === "ledger" && <LedgerView />}
      {tab === "snapshots" && <SnapshotsView onChange={loadOverview} />}
      {tab === "adjustments" && <AdjustmentsView overview={overview} onChange={loadOverview} />}
      {tab === "reconcile" && <ReconcileView onChange={loadOverview} />}
      {tab === "compare" && <CompareView />}
      {tab === "legacy" && <LegacyView />}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const cls = tone === "warn" ? "bg-amber-50 text-amber-800 border-amber-200"
    : tone === "ok" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <div className={`px-3 py-1.5 rounded-md border ${cls}`} data-testid={testid}>
      <div className="uppercase tracking-wider text-[9px] opacity-70">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

const SUM_COLS = [
  ["opening", "Opening"], ["inward", "Inward"], ["returns_in", "Returns In"],
  ["transfer_in", "Transfer In"], ["outward", "Outward"], ["transfer_out", "Transfer Out"],
  ["adjustment", "Adjustment"], ["closing", "Closing"],
];

function PositionView({ mode, overview, onChange }) {
  const [date, setDate] = useState(today());
  const [dep, setDep] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqRef = useState(() => ({ n: 0 }))[0];

  const load = useCallback(async () => {
    setLoading(true);
    const seq = ++reqRef.n;
    try {
      const url = mode === "today" ? "/daily-stock/today" : "/daily-stock/historical";
      const params = { dep_status: dep || undefined };
      if (mode !== "today") params.date = date;
      const { data } = await api.get(url, { params });
      if (seq === reqRef.n) setData(data);
    } catch (e) { if (seq === reqRef.n) toast.error(e.response?.data?.detail || "Load failed"); }
    finally { if (seq === reqRef.n) setLoading(false); }
  }, [mode, date, dep, reqRef]);
  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const filteredRows = (data?.rows || []).filter((r) => !q ||
    JSON.stringify([r.product_name, r.sku, r.brand, r.model, r.product_family, r.dep_status])
      .toLowerCase().includes(q.toLowerCase()));

  const d = mode === "today" ? today() : date;
  const snapshot = async () => {
    try {
      const { data } = await api.post("/daily-stock/snapshots", { date: d });
      toast.success(`Snapshot ${data.code} created`);
      onChange?.(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Snapshot failed"); }
  };

  return (
    <div data-testid={`ds-position-${mode}`}>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {mode === "historical" && (
          <div>
            <Label>View stock as of</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-44" data-testid="ds-historical-date" />
          </div>
        )}
        <div className="text-[12px] text-slate-500">
          {mode === "today" ? `Today · ${today()}` : `As of ${date}`}
          {data?.source && <span className="ml-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">{data.source}</span>}
        </div>
        <div>
          <Label>DEP status</Label>
          <select value={dep} onChange={(e) => setDep(e.target.value)}
            className="h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white w-36" data-testid="ds-dep-filter">
            <option value="">All</option>
            <option value="DEP">DEP</option>
            <option value="NON_DEP">Non-DEP</option>
            <option value="NOT_CLASSIFIED">Not classified</option>
          </select>
        </div>
        <div>
          <Label>Search</Label>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, SKU, family…"
            className="w-52" data-testid="ds-search" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => dl(`/daily-stock/export/xlsx?date=${d}`, `daily-stock-${d}.xlsx`)}
            data-testid="ds-download-xlsx"><FileDown className="h-3.5 w-3.5 mr-1" /> XLSX</Button>
          <Button size="sm" variant="outline" onClick={() => dl(`/daily-stock/export/pdf?date=${d}`, `daily-stock-${d}.pdf`)}
            data-testid="ds-download-pdf"><FileDown className="h-3.5 w-3.5 mr-1" /> PDF</Button>
          {mode === "today" && (
            <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
              onClick={snapshot} data-testid="ds-create-snapshot">
              <Camera className="h-3.5 w-3.5 mr-1" /> Create snapshot
            </Button>
          )}
        </div>
      </div>

      {data?.summary && (
        <div className="grid grid-cols-3 gap-2 mb-3" data-testid="ds-dep-summary">
          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="text-[9px] uppercase tracking-wider text-slate-400">Total closing</div>
            <div className="text-lg font-semibold text-slate-800">{fmt(s.closing)}</div>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
            <div className="text-[9px] uppercase tracking-wider text-emerald-600">DEP</div>
            <div className="text-lg font-semibold text-emerald-800">{fmt(s.dep_closing)}</div>
          </div>
          <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2">
            <div className="text-[9px] uppercase tracking-wider text-sky-600">Non-DEP</div>
            <div className="text-lg font-semibold text-sky-800">{fmt(s.non_dep_closing)}</div>
          </div>
        </div>
      )}

      {data?.summary && (
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mb-4" data-testid="ds-summary">
          {SUM_COLS.map(([k, l]) => (
            <div key={k} className="rounded-md border border-slate-200 bg-white px-3 py-2">
              <div className="text-[9px] uppercase tracking-wider text-slate-400">{l}</div>
              <div className="text-lg font-semibold text-slate-800">{fmt(data.summary[k])}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        : <StockTable rows={filteredRows} onClassified={load} />}
    </div>
  );
}

function DepBadge({ status }) {
  const map = {
    DEP: "bg-emerald-100 text-emerald-800",
    NON_DEP: "bg-sky-100 text-sky-800",
    NOT_CLASSIFIED: "bg-amber-100 text-amber-800",
  };
  const norm = status === "DEP" ? "DEP" : status === "NON_DEP" ? "NON_DEP" : "NOT_CLASSIFIED";
  const label = norm === "NON_DEP" ? "NON-DEP" : norm === "DEP" ? "DEP" : "REQUIRED";
  return <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${map[norm]}`}>{label}</span>;
}

function StockTable({ rows, onClassified }) {
  const [openSerials, setOpenSerials] = useState(null);
  const [depEdit, setDepEdit] = useState(null);
  const [sel, setSel] = useState([]);
  if (!rows.length) return <div className="text-sm text-slate-400 py-8 text-center">No stock lines for this position.</div>;

  const setDep = async (r, value) => {
    try {
      await api.post("/daily-stock/classify-dep", { product_id: r.product_id, dep_status: value });
      toast.success("DEP status updated");
      setDepEdit(null); onClassified?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleSel = (pid) => setSel((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const selectUnclassified = () => setSel(rows.filter((r) => r.product_id && r.dep_status === "NOT_CLASSIFIED").map((r) => r.product_id));
  const bulkClassify = async (value) => {
    try {
      const { data } = await api.post("/daily-stock/classify-dep-bulk", { product_ids: sel, dep_status: value });
      toast.success(`Classified ${data.updated} product(s) as ${value === "NON_DEP" ? "NON-DEP" : value}`);
      setSel([]); onClassified?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-2" data-testid="ds-stock-wrap">
      <div className="flex items-center gap-2 text-[12px]" data-testid="ds-bulk-bar">
        <button onClick={selectUnclassified} className="text-emerald-700 hover:underline" data-testid="ds-select-unclassified">
          Select all unclassified
        </button>
        {sel.length > 0 && (
          <>
            <span className="text-slate-500">{sel.length} selected —</span>
            <button onClick={() => bulkClassify("DEP")} className="px-2 py-0.5 rounded bg-emerald-700 text-white text-[11px]" data-testid="ds-bulk-dep">Classify DEP</button>
            <button onClick={() => bulkClassify("NON_DEP")} className="px-2 py-0.5 rounded bg-sky-700 text-white text-[11px]" data-testid="ds-bulk-nondep">Classify NON-DEP</button>
            <button onClick={() => setSel([])} className="text-slate-500 hover:underline">Clear</button>
          </>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="ds-stock-table">
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
            <tr>
              <th className="px-2 py-2"></th>
              {["Family", "Product", "SKU / OEM", "DEP Status", "Track", "Own", "Open", "In", "Ret", "TfrIn", "Out", "TfrOut", "Adj", "Close", "Avail", "Exc"].map((h) => (
                <th key={h} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-slate-50" data-testid={`ds-row-${i}`}>
                <td className="px-2 py-1.5">
                  {r.product_id && (
                    <input type="checkbox" checked={sel.includes(r.product_id)}
                      onChange={() => toggleSel(r.product_id)} data-testid={`ds-check-${i}`} />
                  )}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700" data-testid={`ds-family-${i}`}>{r.product_family || "OTHERS"}</span>
                </td>
                <td className="px-2 py-1.5 max-w-[220px] truncate" title={r.product_name}>{r.product_name}</td>
                <td className="px-2 py-1.5 font-mono text-[11px]">{r.sku || "—"}</td>
                <td className="px-2 py-1.5" data-testid={`ds-dep-${i}`}>
                  {depEdit === i ? (
                    <select autoFocus defaultValue={r.dep_status} data-testid={`ds-dep-select-${i}`}
                      onChange={(e) => setDep(r, e.target.value)} onBlur={() => setDepEdit(null)}
                      className="h-7 border border-slate-200 rounded px-1 text-[11px] bg-white">
                      <option value="DEP">DEP</option>
                      <option value="NON_DEP">NON-DEP</option>
                      <option value="NOT_CLASSIFIED">Not classified</option>
                    </select>
                  ) : r.product_id ? (
                    <button onClick={() => setDepEdit(i)} data-testid={`ds-dep-btn-${i}`} title="Click to classify">
                      <DepBadge status={r.dep_status} />
                    </button>
                  ) : (
                    <DepBadge status={r.dep_status} />
                  )}
                </td>
                <td className="px-2 py-1.5"><span className="text-[9px] px-1 rounded bg-slate-100">{r.tracking}</span></td>
                <td className="px-2 py-1.5 text-[10px]">{(r.ownership || "").replace("_", " ")}</td>
                <td className="px-2 py-1.5 text-right">{fmt(r.opening)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">{fmt(r.inward)}</td>
                <td className="px-2 py-1.5 text-right">{fmt(r.returns_in)}</td>
                <td className="px-2 py-1.5 text-right">{fmt(r.transfer_in)}</td>
                <td className="px-2 py-1.5 text-right text-rose-700">{fmt(r.outward)}</td>
                <td className="px-2 py-1.5 text-right">{fmt(r.transfer_out)}</td>
                <td className="px-2 py-1.5 text-right">{fmt(r.adjustment)}</td>
                <td className="px-2 py-1.5 text-right font-semibold">
                  {r.tracking === "SERIAL" && r.serial_list?.length ? (
                    <button className="underline decoration-dotted" onClick={() => setOpenSerials(r)}
                      data-testid={`ds-serials-${i}`}>{fmt(r.closing)}</button>
                  ) : fmt(r.closing)}
                </td>
                <td className="px-2 py-1.5 text-right">{fmt(r.available)}</td>
                <td className="px-2 py-1.5">
                  {r.exceptions?.length ? <span className="text-amber-700 text-[10px]" title={r.exceptions.join(", ")}><AlertTriangle className="h-3 w-3 inline" /></span> : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {openSerials && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={() => setOpenSerials(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-4" onClick={(e) => e.stopPropagation()} data-testid="ds-serials-modal">
            <div className="font-semibold text-sm mb-2">{openSerials.product_name} — {openSerials.serial_list.length} serial(s)</div>
            <div className="max-h-72 overflow-y-auto grid grid-cols-2 gap-1 text-[11px] font-mono">
              {openSerials.serial_list.map((s) => <div key={s} className="px-2 py-1 bg-slate-50 rounded">{s}</div>)}
            </div>
            <Button className="mt-3 w-full" variant="outline" onClick={() => setOpenSerials(null)}>Close</Button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

function LedgerView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/daily-stock/movements", { params: { limit: 200 } });
      setRows(data.items || []);
    })();
  }, []);
  const filtered = rows.filter((m) => !q || JSON.stringify(m).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="ds-ledger">
      <Input placeholder="Filter by product, serial, type…" value={q} onChange={(e) => setQ(e.target.value)}
        className="max-w-sm mb-3" data-testid="ds-ledger-filter" />
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
            <tr>{["Movement", "When", "Dir", "Type", "Product", "Qty", "Serials", "By"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((m, i) => (
              <tr key={m.id} className="hover:bg-slate-50" data-testid={`ds-mov-${i}`}>
                <td className="px-2 py-1.5 font-mono text-[10px]">{m.code}</td>
                <td className="px-2 py-1.5 text-[10px] whitespace-nowrap">{(m.ts || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-2 py-1.5"><span className={`text-[10px] px-1 rounded ${m.direction === "IN" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{m.direction}</span></td>
                <td className="px-2 py-1.5 text-[10px]">{m.movement_type}</td>
                <td className="px-2 py-1.5 max-w-[200px] truncate" title={m.product_name}>{m.product_name}</td>
                <td className="px-2 py-1.5 text-right">{m.quantity}</td>
                <td className="px-2 py-1.5 text-[10px] max-w-[160px] truncate">{(m.serials || []).join(", ")}</td>
                <td className="px-2 py-1.5 text-[10px]">{m.performed_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SnapshotsView({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/daily-stock/snapshots"); setRows(data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);
  const supersede = async (s) => {
    const reason = window.prompt("Reason for superseding this snapshot?");
    if (!reason) return;
    try { await api.post(`/daily-stock/snapshots/${s.id}/supersede`, { reason }); toast.success("Superseding snapshot created"); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="ds-snapshots">
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
            <tr>{["Snapshot ID", "Date", "Status", "Recon", "Source", "Closing", "Certified by", "Actions"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((s, i) => (
              <tr key={s.id} className={`hover:bg-slate-50 ${s.superseded_by ? "opacity-50" : ""}`} data-testid={`ds-snap-${i}`}>
                <td className="px-2 py-1.5 font-mono text-[11px]">{s.code}</td>
                <td className="px-2 py-1.5">{s.date}</td>
                <td className="px-2 py-1.5"><span className="text-[10px] px-1 rounded bg-slate-100">{s.status}{s.superseded_by ? " · SUPERSEDED" : ""}</span></td>
                <td className="px-2 py-1.5 text-[10px]">{s.recon_status}</td>
                <td className="px-2 py-1.5 text-[10px]">{s.source || "LIVE"}</td>
                <td className="px-2 py-1.5 text-right">{fmt(s.summary?.closing)}</td>
                <td className="px-2 py-1.5 text-[10px]">{s.certified_by || "—"}</td>
                <td className="px-2 py-1.5">
                  {!s.superseded_by && <button className="text-[11px] text-blue-700 hover:underline" onClick={() => supersede(s)} data-testid={`ds-supersede-${i}`}>Supersede</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReconcileView({ onChange }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/daily-stock/snapshots"); setRows((data.items || []).filter((s) => !s.superseded_by));
  }, []);
  useEffect(() => { load(); }, [load]);
  const setRecon = async (s, status) => {
    try { await api.post(`/daily-stock/snapshots/${s.id}/reconcile`, { status }); toast.success(`Marked ${status}`); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const certify = async (s) => {
    try { await api.post(`/daily-stock/snapshots/${s.id}/certify`, {}); toast.success("Certified"); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="ds-reconcile" className="space-y-2">
      {rows.map((s, i) => (
        <div key={s.id} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2" data-testid={`ds-recon-${i}`}>
          <div className="font-mono text-[11px]">{s.code}</div>
          <div className="text-[12px] text-slate-500">{s.date}</div>
          <div className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{s.recon_status}</div>
          <div className="text-[11px] text-slate-500">Closing {fmt(s.summary?.closing)} · Exc {s.summary?.exceptions ?? 0}</div>
          <div className="ml-auto flex gap-1">
            {s.recon_status !== "CERTIFIED" && <>
              <Button size="sm" variant="outline" onClick={() => setRecon(s, "BALANCED")} data-testid={`ds-recon-balanced-${i}`}>Balanced</Button>
              <Button size="sm" variant="outline" onClick={() => setRecon(s, "REVIEW_REQUIRED")}>Review</Button>
              <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => certify(s)} data-testid={`ds-certify-${i}`}>
                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Certify
              </Button>
            </>}
            {s.recon_status === "CERTIFIED" && <span className="text-emerald-700 text-[12px] flex items-center gap-1"><ShieldCheck className="h-4 w-4" /> Certified by {s.certified_by}</span>}
          </div>
        </div>
      ))}
      {!rows.length && <div className="text-sm text-slate-400 py-8 text-center">No snapshots yet — create one from the Today tab.</div>}
    </div>
  );
}

function AdjustmentsView({ overview, onChange }) {
  const [rows, setRows] = useState([]);
  const [wh, setWh] = useState([]);
  const [form, setForm] = useState({ product_id: "", warehouse_id: "", direction: "IN", quantity: 1, reason: "PHYSICAL_STOCK_CORRECTION", evidence: "" });
  const [prodQ, setProdQ] = useState("");
  const [prods, setProds] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/daily-stock/adjustments"); setRows(data.items || []);
  }, []);
  useEffect(() => { load(); (async () => {
    try { const { data } = await api.get("/inventory/warehouses"); setWh(Array.isArray(data) ? data : (data.items || [])); } catch { /* */ }
  })(); }, [load]);
  useEffect(() => {
    if (prodQ.length < 2) { setProds([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/inventory/products", { params: { q: prodQ, limit: 10 } }); setProds(Array.isArray(data) ? data : (data.items || [])); } catch { /* */ }
    }, 300);
    return () => clearTimeout(t);
  }, [prodQ]);
  const submit = async () => {
    if (!form.product_id || !form.warehouse_id) { toast.error("Pick a product and warehouse"); return; }
    if (!form.evidence.trim()) { toast.error("Evidence is required"); return; }
    try { await api.post("/daily-stock/adjustments", { ...form, quantity: Number(form.quantity) }); toast.success("Adjustment requested"); setForm((f) => ({ ...f, quantity: 1, evidence: "" })); setProdQ(""); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approve = async (a) => {
    try { await api.post(`/daily-stock/adjustments/${a.id}/approve`); toast.success("Approved & posted to ledger"); load(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="ds-adjustments" className="grid md:grid-cols-2 gap-6">
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="font-semibold text-sm mb-3 flex items-center gap-1"><PlusCircle className="h-4 w-4" /> Raise adjustment</div>
        <div className="space-y-3">
          <div className="relative">
            <Label>Product</Label>
            <Input value={form.product_id ? (form._pname || "Selected") : prodQ} onChange={(e) => { setProdQ(e.target.value); setForm((f) => ({ ...f, product_id: "", _pname: "" })); }}
              placeholder="Search product…" data-testid="ds-adj-product" />
            {prods.length > 0 && !form.product_id && (
              <div className="absolute z-20 bg-white border rounded shadow max-h-48 overflow-y-auto w-full mt-1">
                {prods.map((p) => (
                  <button key={p.id} className="block w-full text-left px-2 py-1 text-[12px] hover:bg-slate-100"
                    onClick={() => { setForm((f) => ({ ...f, product_id: p.id, _pname: p.product_name })); setProds([]); }}
                    data-testid={`ds-adj-prod-opt-${p.id}`}>{p.product_name}</button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Warehouse</Label>
            <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white"
              value={form.warehouse_id} onChange={(e) => setForm((f) => ({ ...f, warehouse_id: e.target.value }))} data-testid="ds-adj-warehouse">
              <option value="">Select…</option>
              {wh.map((w) => <option key={w.id} value={w.id}>{w.name || w.code}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Direction</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={form.direction} onChange={(e) => setForm((f) => ({ ...f, direction: e.target.value }))} data-testid="ds-adj-direction">
                <option value="IN">IN</option><option value="OUT">OUT</option>
              </select></div>
            <div><Label>Qty</Label><Input type="number" min={1} value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} data-testid="ds-adj-qty" /></div>
            <div><Label>Reason</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} data-testid="ds-adj-reason">
                {(overview?.adjust_reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
              </select></div>
          </div>
          <div><Label>Evidence / justification *</Label><Input value={form.evidence} onChange={(e) => setForm((f) => ({ ...f, evidence: e.target.value }))} placeholder="e.g. Physical count sheet #12" data-testid="ds-adj-evidence" /></div>
          <Button className="w-full bg-emerald-700 hover:bg-emerald-800 text-white" onClick={submit} data-testid="ds-adj-submit">Request adjustment</Button>
        </div>
      </div>
      <div>
        <div className="font-semibold text-sm mb-3">Adjustment requests</div>
        <div className="space-y-2">
          {rows.map((a, i) => (
            <div key={a.id} className="border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2 text-[12px]" data-testid={`ds-adj-row-${i}`}>
              <span className="font-mono text-[10px]">{a.code}</span>
              <span className={`px-1 rounded text-[10px] ${a.direction === "IN" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{a.direction} {a.quantity}</span>
              <span className="truncate max-w-[160px]">{a.product_name}</span>
              <span className="text-[10px] text-slate-400">{a.reason}</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{a.status}</span>
              {a.status === "PENDING" && <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white h-6" onClick={() => approve(a)} data-testid={`ds-adj-approve-${i}`}>Approve</Button>}
            </div>
          ))}
          {!rows.length && <div className="text-sm text-slate-400 py-6 text-center">No adjustments yet.</div>}
        </div>
      </div>
    </div>
  );
}

function CompareView() {
  const [a, setA] = useState(today());
  const [b, setB] = useState(today());
  const [res, setRes] = useState(null);
  const run = async () => {
    try { const { data } = await api.get("/daily-stock/compare", { params: { date_a: a, date_b: b } }); setRes(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="ds-compare">
      <div className="flex items-end gap-3 mb-4">
        <div><Label>Date A</Label><Input type="date" value={a} onChange={(e) => setA(e.target.value)} className="w-44" data-testid="ds-compare-a" /></div>
        <div><Label>Date B</Label><Input type="date" value={b} onChange={(e) => setB(e.target.value)} className="w-44" data-testid="ds-compare-b" /></div>
        <Button onClick={run} data-testid="ds-compare-run">Compare</Button>
      </div>
      {res && (
        <div className="border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[9px] tracking-wider">
              <tr>{["Product", "Brand", `Closing ${a}`, `Closing ${b}`, "Difference"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {res.changes.map((c, i) => (
                <tr key={i} data-testid={`ds-compare-row-${i}`}>
                  <td className="px-2 py-1.5">{c.product_name}</td>
                  <td className="px-2 py-1.5">{c.brand}</td>
                  <td className="px-2 py-1.5 text-right">{fmt(c.previous_closing)}</td>
                  <td className="px-2 py-1.5 text-right">{fmt(c.current_closing)}</td>
                  <td className={`px-2 py-1.5 text-right font-semibold ${c.difference < 0 ? "text-rose-700" : "text-emerald-700"}`}>{c.difference > 0 ? "+" : ""}{fmt(c.difference)}</td>
                </tr>
              ))}
              {!res.changes.length && <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400">No differences between these dates.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LegacyView() {
  const [batch, setBatch] = useState(null);
  const [mapping, setMapping] = useState({});
  const [stockDate, setStockDate] = useState(today());
  const [preview, setPreview] = useState(null);
  const [batches, setBatches] = useState([]);
  const loadBatches = useCallback(async () => {
    const { data } = await api.get("/daily-stock/legacy/batches"); setBatches(data.items || []);
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/daily-stock/legacy/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setBatch(data); setMapping({}); setPreview(null); toast.success("File read — map the columns"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };
  const saveMapping = async () => {
    try { await api.post(`/daily-stock/legacy/${batch.id}/mapping`, { mapping, stock_date: stockDate }); const { data } = await api.get(`/daily-stock/legacy/${batch.id}/preview`); setPreview(data); toast.success("Preview ready"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doImport = async () => {
    try { const { data } = await api.post(`/daily-stock/legacy/${batch.id}/import`); toast.success(`Imported → ${data.code} (${data.discrepancies} discrepancies)`); setBatch(null); setPreview(null); loadBatches(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="ds-legacy" className="space-y-5">
      <div className="border border-slate-200 rounded-lg p-4">
        <div className="font-semibold text-sm mb-1">Import a legacy Daily Stock sheet</div>
        <p className="text-[12px] text-slate-500 mb-3">Upload your existing Excel/CSV, map the columns to ONE fields, preview against the stock formula, then import as dated historical evidence. The original file is kept, and live stock is never overwritten.</p>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={upload} data-testid="ds-legacy-file" className="text-[12px]" />
      </div>

      {batch && (
        <div className="border border-slate-200 rounded-lg p-4" data-testid="ds-legacy-mapping">
          <div className="font-semibold text-sm mb-2">Map columns — {batch.code}</div>
          <div className="mb-3"><Label>Stock date</Label><Input type="date" value={stockDate} onChange={(e) => setStockDate(e.target.value)} className="w-44" data-testid="ds-legacy-date" /></div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {(batch.target_fields || []).map((f) => (
              <div key={f}>
                <Label className="capitalize">{f.replace("_", " ")}</Label>
                <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[12px] bg-white"
                  value={mapping[f] || ""} onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}
                  data-testid={`ds-legacy-map-${f}`}>
                  <option value="">—</option>
                  {(batch.columns || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          <Button className="mt-3" onClick={saveMapping} data-testid="ds-legacy-preview-btn">Save mapping & preview</Button>
        </div>
      )}

      {preview && (
        <div className="border border-slate-200 rounded-lg p-4" data-testid="ds-legacy-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-sm">Preview ({preview.row_count} rows · {preview.discrepancies} discrepancies)</div>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={doImport} data-testid="ds-legacy-import-btn">Import as snapshot</Button>
          </div>
          <div className="overflow-x-auto max-h-72">
            <table className="w-full text-[12px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[9px]"><tr>{["Product", "Open", "In", "Out", "Sheet close", "Computed", "Match"].map((h) => <th key={h} className="px-2 py-1 text-left">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {preview.rows.map((r, i) => (
                  <tr key={i} className={r.mismatch ? "bg-amber-50" : ""} data-testid={`ds-legacy-row-${i}`}>
                    <td className="px-2 py-1">{r.product_name}</td>
                    <td className="px-2 py-1 text-right">{r.opening}</td>
                    <td className="px-2 py-1 text-right">{r.inward}</td>
                    <td className="px-2 py-1 text-right">{r.outward}</td>
                    <td className="px-2 py-1 text-right">{r.closing_sheet ?? "—"}</td>
                    <td className="px-2 py-1 text-right">{r.closing_computed}</td>
                    <td className="px-2 py-1">{r.mismatch ? <span className="text-amber-700 text-[10px]">MISMATCH</span> : <span className="text-emerald-700 text-[10px]">OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="font-semibold text-sm mb-2">Import history</div>
        <div className="space-y-1">
          {batches.map((b, i) => (
            <div key={b.id} className="flex items-center gap-3 text-[12px] border border-slate-200 rounded px-3 py-1.5" data-testid={`ds-legacy-batch-${i}`}>
              <span className="font-mono text-[10px]">{b.code}</span>
              <span>{b.filename}</span>
              <span className="text-slate-400">{b.stock_date || "—"}</span>
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-100">{b.status}</span>
            </div>
          ))}
          {!batches.length && <div className="text-sm text-slate-400 py-4 text-center">No legacy imports yet.</div>}
        </div>
      </div>
    </div>
  );
}
